import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AuthService } from 'src/app/modules/auth/services/auth/auth.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { XdsButtonComponent, XdsIconButtonComponent, XdsBannerComponent } from '../../../../shared/ui';

@Component({
    selector: 'app-email-confirmation',
    templateUrl: './email-confirmation.component.html',
    styleUrls: ['./email-confirmation.component.css'],
    imports: [CommonModule, RouterModule, TranslateModule, XdsButtonComponent, XdsIconButtonComponent, XdsBannerComponent]
})
export class EmailConfirmationComponent implements OnInit {
  message: string = 'Please wait...';
  errorStatus = { showError: false, errorMessage: '' };
  isSuccess = false;
  /** Konto war schon bestätigt — weder Erfolg noch Fehler, sondern ein eigener, ruhiger Zustand. */
  isAlreadyConfirmed = false;
  /** Erläuternder Satz unter der Überschrift; heute nur im Fall `isAlreadyConfirmed` gesetzt. */
  hint = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private translate: TranslateService,
    public themeService: ThemeService
  ) {}

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  getThemeIconClass(): string {
    const mode = this.themeService.getThemeIcon();
    if (mode === 'brightness_auto') return 'fa-circle-half-stroke';
    if (mode === 'light_mode') return 'fa-sun';
    return 'fa-moon';
  }

  isLoading(): boolean {
    return this.message === 'Please wait...';
  }

  ngOnInit(): void {
    // Token und Email aus Query-Parametern holen
    // Die Tokens sind bereits URL-decoded durch Angular Router
    const token = this.route.snapshot.queryParams['token'];
    const email = this.route.snapshot.queryParams['email'];

    // Validierung
    if (!token || !email) {
      console.error('Missing token or email parameters');
      this.handleError('MISSING_PARAMETERS');
      return;
    }

    // Token direkt verwenden - Angular hat bereits URL-decoding gemacht
    // Simulate loading for better UX
    setTimeout(() => {
      this.confirmEmail(token, email);
    }, 1000);
  }

  private confirmEmail(token: string, email: string): void {
    this.authService.confirmEmail(token, email).subscribe({
      next: (outcome) => {
        if (outcome.ok) {
          this.isSuccess = true;
          this.message = this.translate.instant('REGISTRATION SUCCESSFUL');
          this.errorStatus.showError = false;
          return;
        }

        // Ein bereits bestätigtes Konto ist kein Fehlerfall: Der Link wurde nur ein zweites Mal
        // geöffnet. Vorher landete auch dieser Nutzer bei „Link ungültig — fordere einen neuen an",
        // obwohl sein Konto längst funktioniert und der Server das ausdrücklich als Warnung meldet.
        if (outcome.reason === 'already-confirmed') {
          this.isAlreadyConfirmed = true;
          this.isSuccess = false;
          this.errorStatus.showError = false;
          this.message = this.translate.instant('EMAIL_ALREADY_CONFIRMED_TITLE');
          this.hint = this.translate.instant('EMAIL_ALREADY_CONFIRMED');
          return;
        }

        console.error('Confirmation failed:', outcome.reason);
        this.handleError('CONFIRMATION_FAILED');
      },
      error: (error) => {
        console.error('Email confirmation error:', error);
        this.handleError('CONFIRMATION_FAILED');
      }
    });
  }

  private handleError(errorType: string = 'GENERAL'): void {
    this.errorStatus.showError = true;
    this.isSuccess = false;

    switch (errorType) {
      case 'MISSING_PARAMETERS':
        this.errorStatus.errorMessage = this.translate.instant(
          'INVALID_CONFIRMATION_LINK'
        );
        this.message = this.translate.instant('CONFIRMATION_FAILED');
        break;
      case 'CONFIRMATION_FAILED':
        this.errorStatus.errorMessage = this.translate.instant(
          'THE CONFIRMATION LINK IS INVALID OR HAS EXPIRED. PLEASE REQUEST A NEW ONE.'
        );
        this.message = this.translate.instant('CONFIRMATION_FAILED');
        break;
      default:
        this.errorStatus.errorMessage = this.translate.instant(
          'THERE WAS AN ERROR CONFIRMING YOUR EMAIL'
        );
        this.message = this.translate.instant('CONFIRMATION_FAILED');
    }
  }

  public navigateTo(route: string): void {
    this.router.navigateByUrl(route);
  }
}
